import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'informed';
import { TextField } from '@ui-modules/informed';
import { IDropdownOption } from '@ui-modules/types';
import './styles.scss';

import { IVehicleCosts, IVehicleType } from '@common/interfaces';
import { RadioGroup } from '@components';
import { Vehicle } from '@assets/svg/icons';

type IExamples = {
  [key: string]: string;
};

const examples: IExamples = {
  '1': 'Example: Innova, Passat, etc.',
  '2': 'Example: Mahindra, Patrol, etc.',
  '3': 'Example: Hilux, LC 79 PU DC, etc.',
  '4': 'Example: Hiace, Hyundai H1, etc.',
  '5': 'Example: LC 76, LC 200, etc.',
};

interface VehicleCostsComponent {
  fuelInitial: IDropdownOption;
  fuelOptions: IDropdownOption[];
  vehicleCosts: IVehicleCosts[];
  vehicleTypes: IVehicleType[];
  onChangeFuelType: (fuelType: string) => void;
}

const VehicleCosts: FC<VehicleCostsComponent> = ({
  fuelInitial,
  fuelOptions,
  vehicleCosts,
  vehicleTypes,
  onChangeFuelType,
}) => {
  const { t } = useTranslation();

  return (
    <div className="vehicle-costs">
      <div className="vehicle-costs-header">
        <h3 className="vehicle-costs-header-title">
          <Vehicle />
          {t('mobility.costPreviewByVehicleType')}
        </h3>

        {fuelOptions ? (
          <Form className="fuel-form">
            <RadioGroup
              initialValue={fuelInitial?.value}
              name="fuelType"
              options={fuelOptions}
              onChange={(fieldState) => {
                onChangeFuelType(fieldState.value as string);
              }}
            />
          </Form>
        ) : null}
      </div>

      <div className="vehicle-costs-content">
        {vehicleCosts.map((vehicleCost) => {
          const { averageKmPerHour, body, id, kgCo2Emission, perHour, perKm, percentageOfBuffer } =
            vehicleCost;

          const vehicleType = vehicleTypes?.find(({ value }) => +value === body);

          // TO DO: We need URLs for images from backend
          const customClass = `type_${body}`;

          const customExample = examples[body];

          return (
            <div className="item" key={id}>
              <div className="side">
                <div className="image">
                  <div className={`car ${customClass}`} />
                </div>
                <div className="type">
                  <div className="label">{t('mobility.vehicleType')}</div>
                  <div className="value">{vehicleType?.display_name}</div>
                  <div className="example">{customExample}</div>
                </div>
              </div>

              <Form className="vehicle-costs-form">
                <div className="field" key={id + perKm}>
                  <TextField
                    disabled
                    initialValue={perKm.toString()}
                    label={`${t('mobility.inputCost')} Km`}
                    name="perKm"
                    type="number"
                  />
                </div>
                <div className="field" key={id + perHour}>
                  <TextField
                    disabled
                    initialValue={perHour.toString()}
                    label={`${t('mobility.inputCost')} Hr`}
                    name="perHour"
                    type="number"
                  />
                </div>
                <div className="field" key={id + averageKmPerHour}>
                  <TextField
                    disabled
                    initialValue={averageKmPerHour.toString()}
                    label={t('mobility.inputAverageKmPerHour')}
                    name="averageKmPerHour"
                    type="number"
                  />
                </div>
                <div className="field" key={id + kgCo2Emission}>
                  <TextField
                    disabled
                    initialValue={kgCo2Emission.toString()}
                    label="Kg CO2e/lt"
                    name="kgCo2Emission"
                    type="number"
                  />
                </div>
                <div className="field" key={id + percentageOfBuffer}>
                  <TextField
                    disabled
                    initialValue={percentageOfBuffer.toString()}
                    name="percentageOfBuffer"
                    label={`${t('mobility.inputBuffer')} %`}
                    type="number"
                  />
                </div>
              </Form>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VehicleCosts;
